import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle, MessageCircle, Sparkles, ArrowRight } from 'lucide-react';
import { SEO } from '@/components/seo/SEO';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';
import { pricingPackages } from '@/config/pricingPackages';

// Translations
const translations = {
  nl: {
    eyebrow: 'Prijzen',
    title: 'Een professionele website die met je bedrijf kan meegroeien',
    intro1: 'Van een sterke online aanwezigheid tot een complete digitale werkomgeving. Kies de oplossing die past bij jouw bedrijf. Alle websites worden professioneel ontworpen, mobiel geoptimaliseerd en opgebouwd met een solide SEO-basis.',
    intro2: 'Alle prijzen zijn exclusief btw.',
    mostChosen: 'Meest gekozen',
    oneTimeLabel: 'Eenmalige prijs',
    monthlyLabel: 'Maandelijkse kosten',
    monthlyOptional: '(optioneel)',
    exclVat: 'excl. btw',
    hostingTitle: 'Maandelijkse kosten & hosting',
    hostingIntro: 'Naast de eenmalige opstartkost kun je kiezen voor een maandelijkse bijdrage voor hosting, domein en ondersteuning. Wij werken met een professionele hostingpartner zodat je website snel, veilig en altijd bereikbaar is, en goed samenwerkt met onze digitale oplossingen.',
    hostingFeatures: [
      'Hosting van je website op snelle servers met SSL-beveiliging',
      'Domeinnaam (.be, .nl of andere extensie) met automatische verlenging',
      'Technische updates en back-ups',
      'Kleine tekst- of beeldwijzigingen binnen een fair use-limiet',
      'Ondersteuning via e-mail of WhatsApp'
    ],
    hostingNote: 'Liever meteen contact? Gebruik gerust de chatbot op onze website om je project toe te lichten, een richtprijs te vragen of direct een offerteaanvraag door te sturen. Zo kunnen we je sneller helpen.',
    depositNote: '50% voorschot wordt gevraagd bij aanvang van het project. Het resterende bedrag wordt gefactureerd bij oplevering.',
    ctaTitle: 'Niet zeker welk pakket bij jouw bedrijf past?',
    ctaText: 'Geen enkel bedrijf werkt exact hetzelfde. Daarom bekijken we eerst wat je werkelijk nodig hebt en adviseren we welke oplossing daarbij past. Je ontvangt vooraf een duidelijke prijsopgave, zonder onverwachte kosten en zonder functies waarvoor je niet betaalt.',
    ctaButton: 'Vraag vrijblijvend een voorstel aan',
    chatbotCta: 'of bereken je voorstel via onze chatbot',
    chatbotButton: 'Start chatbot offerte',
    faqTitle: 'Veelgestelde vragen',
    seoTitle: 'Prijzen & Pakketten - Website laten maken',
    seoDesc: 'Bekijk onze transparante websitepakketten: Starter vanaf €599, Business vanaf €1.099, Business Portal vanaf €1.995 en Business Pro vanaf €2.995.'
  },
  en: {
    eyebrow: 'Pricing',
    title: 'A professional website that grows with your business',
    intro1: 'From a strong online presence to a complete digital workspace. Choose the solution that fits your business. All websites are professionally designed, mobile-optimized and built on a solid SEO foundation.',
    intro2: 'All prices are excluding VAT.',
    mostChosen: 'Most popular',
    oneTimeLabel: 'One-time price',
    monthlyLabel: 'Monthly costs',
    monthlyOptional: '(optional)',
    exclVat: 'excl. VAT',
    hostingTitle: 'Monthly costs & hosting',
    hostingIntro: 'In addition to the one-time startup cost, you can choose a monthly contribution for hosting, domain and support. We work with a professional hosting partner so your website is fast, secure and always accessible.',
    hostingFeatures: [
      'Website hosting on fast servers with SSL security',
      'Domain name (.be, .nl or other extension) with automatic renewal',
      'Technical updates and backups',
      'Small text or image changes within a fair use limit',
      'Support via email or WhatsApp'
    ],
    hostingNote: 'Prefer immediate contact? Feel free to use the chatbot on our website to explain your project, ask for a ballpark price or submit a quote request directly.',
    depositNote: '50% deposit is required at the start of the project. The remaining amount will be invoiced upon delivery.',
    ctaTitle: 'Not sure which package fits your business?',
    ctaText: 'No two businesses are exactly alike. That\'s why we first look at what you really need and advise which solution fits. You receive a clear quote in advance, with no unexpected costs and no features you don\'t pay for.',
    ctaButton: 'Request a no-obligation proposal',
    chatbotCta: 'or calculate your proposal via our chatbot',
    chatbotButton: 'Start chatbot quote',
    faqTitle: 'Frequently asked questions',
    seoTitle: 'Pricing & Packages - Web Design',
    seoDesc: 'View our transparent website packages: Starter from €599, Business from €1,099, Business Portal from €1,995 and Business Pro from €2,995.'
  },
  fr: {
    eyebrow: 'Tarifs',
    title: 'Un site professionnel qui grandit avec votre entreprise',
    intro1: 'D\'une forte présence en ligne à un environnement de travail digital complet. Choisissez la solution adaptée à votre entreprise. Tous les sites sont conçus professionnellement, optimisés mobile et bâtis sur une base SEO solide.',
    intro2: 'Tous les prix sont hors TVA.',
    mostChosen: 'Le plus choisi',
    oneTimeLabel: 'Prix unique',
    monthlyLabel: 'Coûts mensuels',
    monthlyOptional: '(optionnel)',
    exclVat: 'HTVA',
    hostingTitle: 'Coûts mensuels & hébergement',
    hostingIntro: 'En plus du coût de démarrage unique, vous pouvez opter pour une contribution mensuelle.',
    hostingFeatures: [
      'Hébergement sur serveurs rapides avec sécurité SSL',
      'Nom de domaine avec renouvellement automatique',
      'Mises à jour techniques et sauvegardes',
      'Petites modifications dans la limite du fair use',
      'Support par e-mail ou WhatsApp'
    ],
    hostingNote: 'Vous préférez un contact immédiat? Utilisez le chatbot sur notre site.',
    depositNote: 'Un acompte de 50% est demandé au début du projet. Le solde sera facturé à la livraison.',
    ctaTitle: 'Vous ne savez pas quel forfait convient?',
    ctaText: 'Aucune entreprise ne fonctionne exactement de la même façon. Nous regardons d\'abord ce dont vous avez réellement besoin et conseillons la solution adaptée. Vous recevez un devis clair à l\'avance, sans coûts inattendus.',
    ctaButton: 'Demander une proposition sans engagement',
    chatbotCta: 'ou calculez votre proposition via le chatbot',
    chatbotButton: 'Démarrer devis chatbot',
    faqTitle: 'Questions fréquentes',
    seoTitle: 'Tarifs & Forfaits',
    seoDesc: 'Consultez nos tarifs transparents.'
  },
  de: {
    eyebrow: 'Preise',
    title: 'Eine professionelle Website, die mit Ihrem Unternehmen wächst',
    intro1: 'Von einer starken Online-Präsenz bis zu einer kompletten digitalen Arbeitsumgebung. Wählen Sie die Lösung, die zu Ihrem Unternehmen passt. Alle Websites werden professionell gestaltet, mobil optimiert und auf einer soliden SEO-Basis aufgebaut.',
    intro2: 'Alle Preise verstehen sich zzgl. MwSt.',
    mostChosen: 'Am beliebtesten',
    oneTimeLabel: 'Einmaliger Preis',
    monthlyLabel: 'Monatliche Kosten',
    monthlyOptional: '(optional)',
    exclVat: 'zzgl. MwSt.',
    hostingTitle: 'Monatliche Kosten & Hosting',
    hostingIntro: 'Zusätzlich zu den einmaligen Startkosten können Sie einen monatlichen Beitrag wählen.',
    hostingFeatures: [
      'Website-Hosting auf schnellen Servern mit SSL-Sicherheit',
      'Domainname mit automatischer Verlängerung',
      'Technische Updates und Backups',
      'Kleine Änderungen im Rahmen des Fair Use',
      'Support per E-Mail oder WhatsApp'
    ],
    hostingNote: 'Lieber sofortigen Kontakt? Nutzen Sie den Chatbot auf unserer Website.',
    depositNote: '50% Anzahlung wird bei Projektbeginn fällig. Der Restbetrag wird bei Lieferung in Rechnung gestellt.',
    ctaTitle: 'Nicht sicher, welches Paket zu Ihnen passt?',
    ctaText: 'Kein Unternehmen arbeitet genau gleich. Deshalb schauen wir zuerst, was Sie wirklich brauchen, und beraten die passende Lösung. Sie erhalten vorab ein klares Angebot, ohne unerwartete Kosten.',
    ctaButton: 'Unverbindliches Angebot anfragen',
    chatbotCta: 'oder berechnen Sie Ihr Angebot über den Chatbot',
    chatbotButton: 'Chatbot-Angebot starten',
    faqTitle: 'Häufig gestellte Fragen',
    seoTitle: 'Preise & Pakete',
    seoDesc: 'Sehen Sie unsere transparenten Preise.'
  }
};


// FAQ data
const faqData = {
  nl: [
    { question: 'Wat zit er inbegrepen in de eenmalige prijs?', answer: 'Elk pakket bevat een volledig functionele website inclusief design, ontwikkeling, hosting-advies, SSL-certificaat en basis SEO. Je krijgt ook toegang tot een gebruiksvriendelijk CMS om zelf content aan te passen. Alle prijzen zijn exclusief btw.' },
    { question: 'Moet ik maandelijks betalen voor hosting?', answer: 'De maandelijkse hosting is optioneel maar aanbevolen. Het omvat professionele hosting op snelle servers, SSL-beveiliging, automatische back-ups, domeinnaam met verlenging, en basis ondersteuning. Je kunt ook zelf hosting regelen als je dat verkiest.' },
    { question: 'Hoe lang duurt de oplevering?', answer: 'De doorlooptijd hangt af van het gekozen pakket: Starter binnen 7-10 werkdagen, Business binnen 2-4 weken, en de grotere pakketten afhankelijk van de complexiteit.' },
    { question: 'Kan ik later upgraden naar een groter pakket?', answer: 'Absoluut! Je kunt op elk moment extra pagina\'s, functionaliteiten of een klantzone toevoegen. We rekenen alleen het verschil door.' },
    { question: 'Wat als ik niet tevreden ben?', answer: 'Jouw tevredenheid is onze prioriteit. We werken met revisierondes om ervoor te zorgen dat het eindresultaat perfect aansluit bij je wensen.' }
  ],
  en: [
    { question: 'What\'s included in the one-time price?', answer: 'Each package includes a fully functional website including design, development, hosting advice, SSL certificate and basic SEO. All prices are excluding VAT.' },
    { question: 'Do I have to pay monthly for hosting?', answer: 'Monthly hosting is optional but recommended. It includes professional hosting, SSL security, automatic backups, domain name, and basic support.' },
    { question: 'How long does delivery take?', answer: 'Turnaround time depends on the package: Starter within 7-10 business days, Business within 2-4 weeks.' },
    { question: 'Can I upgrade later?', answer: 'Absolutely! You can add extra pages, features or a client zone at any time. We only charge the difference.' },
    { question: 'What if I\'m not satisfied?', answer: 'Your satisfaction is our priority. We work with revision rounds to ensure the end result perfectly matches your wishes.' }
  ]
};

// CSS-in-JS styles matching the provided CSS
const styles = {
  section: {
    padding: '80px 20px',
    background: 'linear-gradient(180deg, #F9FAFB 0%, #F3F4F6 100%)',
    color: '#111827',
    minHeight: '100vh'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto'
  },
  intro: {
    maxWidth: '760px',
    margin: '0 auto 48px',
    textAlign: 'center'
  },
  eyebrow: {
    display: 'inline-block',
    marginBottom: '12px',
    padding: '8px 14px',
    borderRadius: '999px',
    background: '#dbeafe',
    color: '#2563EB',
    fontSize: '0.85rem',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  title: {
    margin: '0 0 16px',
    fontSize: 'clamp(2rem, 4vw, 3.2rem)',
    lineHeight: '1.1',
    fontWeight: '800',
    color: '#111827'
  },
  introText: {
    margin: '0 auto 16px',
    fontSize: '1.05rem',
    lineHeight: '1.75',
    color: '#4B5563',
    maxWidth: '680px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '24px',
    marginTop: '32px'
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '30px',
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid #dbe3ee',
    borderRadius: '18px',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    backdropFilter: 'blur(10px)',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease'
  },
  cardFeatured: {
    border: '1.5px solid #2563EB',
    boxShadow: '0 18px 45px rgba(37, 99, 235, 0.12)'
  },
  badge: {
    position: 'absolute',
    top: '18px',
    right: '18px',
    background: '#f59e0b',
    color: '#ffffff',
    padding: '7px 12px',
    borderRadius: '999px',
    fontSize: '0.78rem',
    fontWeight: '700',
    letterSpacing: '0.02em'
  },
  cardTitle: {
    margin: '0 0 10px',
    fontSize: '1.55rem',
    fontWeight: '800',
    lineHeight: '1.2',
    color: '#111827'
  },
  cardSubtitle: {
    margin: '0 0 24px',
    color: '#4B5563',
    lineHeight: '1.7',
    fontSize: '0.98rem'
  },
  priceBlock: {
    marginBottom: '24px',
    padding: '18px 20px',
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '12px'
  },
  label: {
    margin: '0 0 8px',
    fontSize: '0.82rem',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#4B5563'
  },
  price: {
    margin: '0',
    fontSize: '2rem',
    lineHeight: '1.1',
    fontWeight: '800',
    color: '#111827'
  },
  priceSmall: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#4B5563'
  },
  featureList: {
    margin: '0 0 24px',
    padding: '0',
    listStyle: 'none',
    display: 'grid',
    gap: '12px'
  },
  featureItem: {
    position: 'relative',
    paddingLeft: '28px',
    lineHeight: '1.7',
    color: '#111827',
    fontSize: '0.95rem'
  },
  checkmark: {
    position: 'absolute',
    left: '0',
    top: '2px',
    width: '20px',
    height: '20px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#dbeafe',
    color: '#2563EB',
    fontSize: '0.78rem',
    fontWeight: '800'
  },
  monthlyNote: {
    margin: '10px 0 0',
    color: '#4B5563',
    lineHeight: '1.6',
    fontSize: '0.95rem'
  },
  extraSection: {
    marginTop: '56px',
    padding: '32px',
    background: '#ffffff',
    border: '1px solid #dbe3ee',
    borderRadius: '18px',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
  },
  extraTitle: {
    margin: '0 0 14px',
    fontSize: '1.5rem',
    fontWeight: '800',
    color: '#111827'
  },
  extraText: {
    margin: '0 0 18px',
    color: '#4B5563',
    lineHeight: '1.75'
  },
  ctaSection: {
    marginTop: '40px',
    textAlign: 'center',
    padding: '40px 28px',
    borderRadius: '24px',
    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    color: '#ffffff',
    boxShadow: '0 20px 50px rgba(37, 99, 235, 0.2)'
  },
  ctaTitle: {
    margin: '0 0 12px',
    fontSize: '1.9rem',
    lineHeight: '1.2',
    fontWeight: '800',
    color: '#ffffff'
  },
  ctaText: {
    maxWidth: '760px',
    margin: '0 auto 22px',
    lineHeight: '1.75',
    color: 'rgba(255, 255, 255, 0.88)'
  },
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
    padding: '14px 24px',
    background: '#ffffff',
    color: '#2563EB',
    borderRadius: '999px',
    fontWeight: '700',
    textDecoration: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease'
  },
  faqSection: {
    marginTop: '64px',
    maxWidth: '800px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  faqTitle: {
    textAlign: 'center',
    marginBottom: '32px',
    fontSize: '1.8rem',
    fontWeight: '800',
    color: '#111827'
  },
  faqItem: {
    background: '#ffffff',
    border: '1px solid #dbe3ee',
    borderRadius: '12px',
    marginBottom: '12px',
    overflow: 'hidden'
  },
  faqQuestion: {
    width: '100%',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#111827'
  },
  faqAnswer: {
    padding: '0 24px 20px',
    color: '#4B5563',
    lineHeight: '1.75'
  }
};

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div style={styles.faqItem}>
    <button style={styles.faqQuestion} onClick={onClick}>
      <span>{question}</span>
      <ChevronDown 
        size={20} 
        style={{ 
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.3s ease',
          color: '#64748b'
        }}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={styles.faqAnswer}>{answer}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const PricingPage = () => {
  const { language } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(0);
  
  const t = translations[language] || translations.nl;
  const currentPackages = pricingPackages[language] || pricingPackages.nl;
  const faqs = faqData[language] || faqData.nl;
  const contactPath = getLocalizedPath('contact', language);

  const monthlyLabels = {
    nl: { manage: 'Beheer & hosting', platform: 'Platform, hosting & support' },
    en: { manage: 'Management & hosting', platform: 'Platform, hosting & support' },
    fr: { manage: 'Gestion & hébergement', platform: 'Plateforme, hébergement & support' },
    de: { manage: 'Verwaltung & Hosting', platform: 'Plattform, Hosting & Support' },
  };
  const mLabel = monthlyLabels[language] || monthlyLabels.nl;

  const openChatbot = () => {
    window.dispatchEvent(new CustomEvent('netblitz:open-quote'));
  };

  return (
    <main data-testid="pricing-page" id="pricing">
      <SEO 
        title={t.seoTitle}
        description={t.seoDesc}
      />
      
      <section style={styles.section}>
        <div style={styles.container}>
          {/* Section Intro */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.intro}
          >
            <span style={styles.eyebrow}>{t.eyebrow}</span>
            <h1 style={styles.title}>{t.title}</h1>
            <p style={styles.introText}>{t.intro1}</p>
            <p style={styles.introText}>{t.intro2}</p>
          </motion.div>

          {/* Pricing Grid */}
          <div style={styles.grid} className="pricing-grid">
            {currentPackages.map((pkg, index) => (
              <motion.article
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                style={{
                  ...styles.card,
                  ...(pkg.popular ? styles.cardFeatured : {})
                }}
                className="pricing-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 18px 45px rgba(15, 23, 42, 0.10)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = pkg.popular 
                    ? '0 18px 45px rgba(15, 118, 110, 0.12)' 
                    : '0 8px 24px rgba(15, 23, 42, 0.06)';
                }}
              >
                {pkg.popular && <div style={styles.badge}>{t.mostChosen}</div>}
                
                <h3 style={styles.cardTitle}>{pkg.name}</h3>
                <p style={styles.cardSubtitle}>{pkg.subtitle}</p>

                <div style={styles.priceBlock}>
                  <p style={styles.label}>{t.oneTimeLabel}</p>
                  <p style={styles.price}>
                    {pkg.price} <span style={styles.priceSmall}>{t.exclVat}</span>
                  </p>
                </div>

                <ul style={styles.featureList}>
                  {pkg.features.map((feature, i) => (
                    <li key={i} style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div style={styles.priceBlock}>
                  <p style={styles.label}>{(pkg.slug === 'portal' || pkg.slug === 'pro') ? mLabel.platform : mLabel.manage}</p>
                  <p style={{...styles.price, fontSize: '1.5rem'}}>
                    {pkg.monthlyPrice} <span style={styles.priceSmall}>{t.exclVat}</span>
                  </p>
                  <p style={styles.monthlyNote}>{pkg.monthlyNote}</p>
                </div>

                <Link
                  to={`${contactPath}?pakket=${encodeURIComponent(pkg.name)}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    width: '100%',
                    minHeight: '52px',
                    padding: '14px 20px',
                    background: pkg.popular
                      ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                      : '#111827',
                    color: '#ffffff',
                    borderRadius: '999px',
                    fontWeight: '700',
                    fontSize: '0.98rem',
                    textDecoration: 'none',
                    boxShadow: pkg.popular ? '0 10px 25px rgba(37,99,235,0.3)' : 'none',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  data-testid={`pricing-cta-${pkg.slug}`}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {pkg.cta}
                  <ArrowRight size={18} />
                </Link>
              </motion.article>
            ))}
          </div>

          {/* Hosting Extra Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={styles.extraSection}
          >
            <h3 style={styles.extraTitle}>{t.hostingTitle}</h3>
            <p style={styles.extraText}>{t.hostingIntro}</p>
            <ul style={{...styles.featureList, marginBottom: '18px'}}>
              {t.hostingFeatures.map((feature, i) => (
                <li key={i} style={styles.featureItem}>
                  <span style={styles.checkmark}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <p style={{...styles.extraText, fontSize: '0.95rem', marginBottom: 0}}>{t.hostingNote}</p>
          </motion.div>

          {/* 50% Deposit Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
              borderRadius: '16px',
              border: '1px solid #F59E0B',
              maxWidth: '700px',
              margin: '40px auto'
            }}
          >
            <span style={{ fontSize: '28px', flexShrink: 0 }}>💡</span>
            <div>
              <p style={{ fontWeight: '700', color: '#92400E', marginBottom: '4px', fontSize: '1rem' }}>
                50% voorschot bij aanvang
              </p>
              <p style={{ color: '#78350F', fontSize: '0.9rem', lineHeight: '1.6' }}>
                {t.depositNote}
              </p>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={styles.ctaSection}
          >
            <h3 style={styles.ctaTitle}>{t.ctaTitle}</h3>
            <p style={styles.ctaText}>{t.ctaText}</p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <Link 
                to={contactPath} 
                style={styles.ctaButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t.ctaButton}
              </Link>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', margin: '8px 0' }}>{t.chatbotCta}</p>
              <button
                onClick={openChatbot}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                  color: '#ffffff',
                  borderRadius: '999px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  boxShadow: '0 8px 20px rgba(249, 115, 22, 0.4)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(249, 115, 22, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(249, 115, 22, 0.4)';
                }}
              >
                <Sparkles size={20} />
                {t.chatbotButton}
                <MessageCircle size={20} />
              </button>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <div style={styles.faqSection}>
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <span style={styles.eyebrow}>
                <HelpCircle size={14} style={{marginRight: '6px', verticalAlign: 'middle'}} />
                FAQ
              </span>
            </div>
            <h2 style={styles.faqTitle}>{t.faqTitle}</h2>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 991px) {
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </main>
  );
};

export default PricingPage;
